import {
  CmpInvoiceStatusEnum,
  CmpMemberDetailFieldsFragment,
  CmpMemberRenewalStepStateEnum,
} from "../../../../graphql/types";
import { formatOrdinalShortDate } from "../../../common/dashboard/formatters";

export type MemberRenewalState = NonNullable<CmpMemberDetailFieldsFragment["renewal"]>;
export type MemberRenewalPreview = NonNullable<CmpMemberDetailFieldsFragment["renewalPreview"]>;

// Update Member Form (CMP Dashboard 6): Step 5 has two subtitles, keyed on
// whether the current-cycle renewal invoice has been generated and sent.
export const memberRenewalSentSubtitle = "A membership renewal invoice has already been generated and sent for this member.\n\nTo make changes, go to Manage Invoices, find the member\u2019s renewal invoice, and void the invoice. Then return to Step 5 \u2013 Membership Renewal, make your changes, and click Create Invoice at the bottom of the page to send to member";
export const memberRenewalScheduledSubtitle = "A membership renewal invoice will be automatically created and sent based on your Chamber Setup settings before the membership expires.\n\nIf you need to update the renewal details before the invoice is generated, make your changes and click Update Membership Renewal Invoice below. The updated invoice will then be sent according to your Chamber Setup settings.";

export const getMemberRenewalPresentation = ({
  renewal,
  autoRenewSelected,
  firstMembership = false,
}: {
  renewal: Pick<MemberRenewalState, "stepState" | "autoRenewEnabled" | "actions"> & {
    cycleInvoice?: Pick<NonNullable<MemberRenewalState["cycleInvoice"]>, "invoiceStatus"> | null;
  };
  /** The Step 5 auto-renew checkbox value; the button label follows it. */
  autoRenewSelected: boolean;
  /** The member has no saved membership (for example a reactivated imported member). */
  firstMembership?: boolean;
}) => {
  const generated = renewal.stepState === CmpMemberRenewalStepStateEnum.InvoiceGenerated;
  const voided = renewal.stepState === CmpMemberRenewalStepStateEnum.InvoiceVoided;
  // Editable saves details for the upcoming automatic invoice (no invoice now).
  // Voided previews, creates and sends the replacement renewal invoice. A first
  // membership has no saved details to schedule, so it uses the same send path.
  const sendsInvoice = voided || (firstMembership && !generated);
  const primaryActions = sendsInvoice ? [renewal.actions.createNow] : [renewal.actions.updateDetails];
  return {
    title: renewal.autoRenewEnabled ? "Membership Set for Auto-Renewal" : "Membership Renewal",
    subtitle: generated ? memberRenewalSentSubtitle : memberRenewalScheduledSubtitle,
    sendsInvoice,
    locked: generated,
    primaryLabel: sendsInvoice
      ? "Send Membership Renewal Invoice"
      : autoRenewSelected
        ? "Update Membership Renewal Details"
        : "Update Membership Renewal Invoice",
    primaryAvailable: !generated && primaryActions.some((action) => action.available),
    blockedReason: !generated && primaryActions.every((action) => !action.available)
      ? primaryActions.find((action) => action.reason)?.reason || null
      : null,
    invoiceCreatedLabel: generated && renewal.cycleInvoice
      ? renewal.cycleInvoice.invoiceStatus === CmpInvoiceStatusEnum.Draft ? "Draft Invoice Created:" : "Invoice Created:"
      : null,
  };
};

/** Update Member status card (Active): sent line and auto-renew badge text. */
export const getMemberRenewalStatusCard = ({
  renewal,
  autoRenewOn,
  timeZone,
}: {
  renewal?: Pick<MemberRenewalState, "stepState" | "renewalInvoiceSentAtUtcMilli"> | null;
  autoRenewOn: boolean;
  timeZone?: string;
}) => ({
  // MEM-N7: shown once the current-cycle renewal invoice has gone out.
  sentLabel: renewal?.stepState === CmpMemberRenewalStepStateEnum.InvoiceGenerated && renewal.renewalInvoiceSentAtUtcMilli
    ? `Renewal invoice sent ${formatOrdinalShortDate(renewal.renewalInvoiceSentAtUtcMilli, timeZone)}`
    : null,
  // MEM-N8 / T15.
  autoRenewLabel: autoRenewOn ? "Credit Card Auto-Renewal Enabled" : "Auto-Renewal Not Enabled",
});

type RenewalRevisionSource = {
  member: Pick<CmpMemberDetailFieldsFragment["member"], "updatedAtUtcMilli" | "createdAtUtcMilli">;
  renewal?: { cycleInvoice?: { id: string; updatedAtUtcMilli?: number | null } | null } | null;
};

export const getMemberRenewalRevision = (detail: RenewalRevisionSource) =>
  detail.member.updatedAtUtcMilli || detail.member.createdAtUtcMilli || 0;

/** Keep a confirmed command readback when Apollo still has an older enclosing detail. */
export const getLatestMemberRenewalDetail = (
  queried: CmpMemberDetailFieldsFragment,
  confirmed: CmpMemberDetailFieldsFragment | null,
) => {
  if (!confirmed || queried.member.id !== confirmed.member.id || queried.member.chamberId !== confirmed.member.chamberId) return queried;
  const queriedRevision = getMemberRenewalRevision(queried);
  const confirmedRevision = getMemberRenewalRevision(confirmed);
  if (confirmedRevision > queriedRevision) return confirmed;
  if (confirmedRevision < queriedRevision) return queried;
  return confirmed.generatedAtUtcMilli > queried.generatedAtUtcMilli ? confirmed : queried;
};

/** Pending renewal edits use their own private payer, including an unknown payer. */
export const getMemberRenewalFormDetail = (
  queried: CmpMemberDetailFieldsFragment,
  confirmed: CmpMemberDetailFieldsFragment | null,
) => {
  const detail = getLatestMemberRenewalDetail(queried, confirmed);
  const pending = detail.renewal?.pendingMembershipChange;
  return pending && detail.member.cmpExtension ? {
    ...detail,
    thirdPartyBillingSnapshot: detail.renewal?.pendingThirdPartyBillingSnapshot || null,
    member: { ...detail.member, cmpExtension: { ...detail.member.cmpExtension, membership: pending.newMembership } },
  } : detail;
};

export const isMemberRenewalPreviewCurrent = ({
  detail,
  preview,
}: {
  detail: RenewalRevisionSource;
  preview: Pick<MemberRenewalPreview, "memberUpdatedAtUtcMilli" | "existingDraftInvoiceId" | "invoiceUpdatedAtUtcMilli">;
}) => {
  if (preview.memberUpdatedAtUtcMilli !== getMemberRenewalRevision(detail)) return false;
  const cycleInvoice = detail.renewal?.cycleInvoice;
  if (preview.existingDraftInvoiceId) {
    return preview.existingDraftInvoiceId === cycleInvoice?.id &&
      preview.invoiceUpdatedAtUtcMilli === cycleInvoice.updatedAtUtcMilli;
  }
  return !cycleInvoice;
};

/** The consent revision can change without changing the reviewed invoice. */
export const hasSameMemberRenewalQuote = (before: MemberRenewalPreview, after: MemberRenewalPreview) => {
  const paymentChoice = (choice: MemberRenewalPreview["membership"]["paymentChoice"]) => choice && ({
    type: choice.type,
    manualPaymentMethodId: choice.manualPaymentMethodId,
    manualPaymentMethodLabel: choice.manualPaymentMethodLabel,
    autoRenew: choice.autoRenew,
  });
  const quote = ({ invoice, membership }: MemberRenewalPreview) => ({
    currency: membership.pricingSnapshot.currency,
    pricing: {
      ...membership.pricingSnapshot,
      lineItems: membership.pricingSnapshot.lineItems?.map(({ id, __typename, ...line }) => line),
    },
    paymentResponsibility: membership.paymentResponsibility,
    paymentSchedule: membership.paymentSchedule,
    membershipPaymentChoice: paymentChoice(membership.paymentChoice),
    invoicePaymentChoice: paymentChoice(invoice.paymentChoice),
    billingSnapshot: invoice.billingSnapshot,
    billToSnapshot: invoice.billToSnapshot,
    lineItems: invoice.lineItems?.map(({ id, __typename, ...line }) => line),
    subtotal: invoice.subtotalAmountInCents,
    tax: invoice.taxAmountInCents,
    total: invoice.totalAmountInCents,
    amountDue: invoice.amountDueInCents,
    waivedAmount: invoice.waivedAmountInCents,
    creditedAmount: invoice.creditedAmountInCents,
    refundedAmount: invoice.refundedAmountInCents,
    dueAtUtcMilli: invoice.dueAtUtcMilli,
    coveragePeriodStartUtcMilli: invoice.coveragePeriodStartUtcMilli,
    coveragePeriodEndUtcMilli: invoice.coveragePeriodEndUtcMilli,
    paymentRules: invoice.paymentRules,
    paymentPlan: invoice.paymentPlan,
  });
  return JSON.stringify(quote(before)) === JSON.stringify(quote(after));
};
